import { Address, User } from '../../../../models/user';

export type ShipmentReturnAddressStrategy = 'company' | 'default' | 'profile';

export interface ShipmentReturnAddressInput {
    user: Pick<User, 'type' | 'company' | 'mainAddress'>;
    companyReturnAddress?: Address | null;
    profileAddresses?: Address[] | null;
}

/**
 * Professional accounts keep a distinct company return address.
 * Personal accounts have one default address, used as shipping, return, and billing.
 * Team accounts keep the previous lookup: a profile address other than the default.
 */
export function shipmentReturnAddressStrategy(user: Pick<User, 'type' | 'company'>): ShipmentReturnAddressStrategy {
    switch (user.type) {
        case 'pro':
            return user.company ? 'company' : 'profile';
        case 'personal':
            return 'default';
        case 'team':
            return 'profile';
        default: {
            const exhaustive: never = user.type;
            return exhaustive;
        }
    }
}

export function resolveShipmentReturnAddress(input: ShipmentReturnAddressInput): Address | null {
    const strategy = shipmentReturnAddressStrategy(input.user);
    switch (strategy) {
        case 'company':
            return input.companyReturnAddress ?? null;
        case 'default':
            return input.user.mainAddress ?? null;
        case 'profile':
            return profileReturnAddressExcludingDefault(input.profileAddresses, input.user.mainAddress?.id);
        default: {
            const exhaustive: never = strategy;
            return exhaustive;
        }
    }
}

export function shipmentReturnAddressBadges(userType: User['type']): Array<'return' | 'default'> {
    switch (userType) {
        case 'personal':
            return ['default'];
        case 'pro':
        case 'team':
            return ['return'];
        default: {
            const exhaustive: never = userType;
            return exhaustive;
        }
    }
}

function profileReturnAddressExcludingDefault(
    addresses: Address[] | null | undefined,
    mainAddressId: number | null | undefined,
): Address | null {
    return addresses?.find((address) => address.profileAddress && address.id !== mainAddressId) ?? null;
}
